import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface GalleryCategory {
  id: 'weddings' | 'kids' | 'corporate' | 'festivals';
  label: string;
}

const tabs: GalleryCategory[] = [
  { id: 'weddings', label: 'Bruiloften' },
  { id: 'kids', label: 'Kinderfeestjes' },
  { id: 'corporate', label: 'Bedrijfsevents' },
  { id: 'festivals', label: 'Festivals' }
];

const galleryData: Record<string, {
  main: { src: string; alt: string; title?: string; caption?: string };
  side: { src: string; alt: string }[];
}> = {
  weddings: {
    main: {
      src: '/images/wedding.png',
      alt: 'Romantische Bruiloft',
      title: '',
      caption: ''
    },
    side: [
      { src: '/images/wedding-golden.png', alt: 'Golden Hour Service' },
      { src: '/images/bitterball.png', alt: 'Midnight Snack' },
      { src: '/images/491442987_17858641950406413_7549773622150705947_n.jpg', alt: 'Toost Moment' },
      { src: '/images/500119355_17862875781406413_7107130305137472544_n.jpg', alt: 'Feestmoment' }
    ]
  },
  kids: {
    main: {
      src: '/images/kids.png',
      alt: 'Vrolijk Kinderfeest',
      title: 'Kids Party',
      caption: 'Plezier gegarandeerd!'
    },
    side: [
      { src: '/images/476760421_17847749799406413_4345076249703767723_n.jpg', alt: 'Kids Fun 1' },
      { src: '/images/479547241_17848040274406413_6366781008384944942_n.jpg', alt: 'Kids Fun 2' },
      { src: '/images/481680149_17850642081406413_2542156037212537063_n.jpg', alt: 'Kids Fun 3' },
      { src: '/images/485013475_17853710805406413_8256604571183439592_n.jpg', alt: 'Kids Fun 4' }
    ]
  },
  corporate: {
    main: {
      src: '/images/bedrijf.png',
      alt: 'Bedrijfsevent',
      title: 'Team Day',
      caption: 'Teams verbinden met lekker eten'
    },
    side: [
      { src: '/images/487922685_17855795181406413_7008286293804022264_n.jpg', alt: 'Team 1' },
      { src: '/images/487999072_17855521356406413_3008742692069600345_n.jpg', alt: 'Team 2' },
      { src: '/images/491418728_17859507246406413_4781809603186001903_n.jpg', alt: 'Team 3' },
      { src: '/images/491442987_17858641950406413_7549773622150705947_n.jpg', alt: 'Team 4' }
    ]
  },
  festivals: {
    main: {
      src: '/images/festival.png',
      alt: 'Festival',
      title: 'Gentse Feesten',
      caption: 'Volume expertise, maximale impact'
    },
    side: [
      { src: '/images/496948084_17861483769406413_289227369687086936_n.jpg', alt: 'Festival 1' },
      { src: '/images/500119355_17862875781406413_7107130305137472544_n.jpg', alt: 'Festival 2' },
      { src: '/images/514541036_17867754138406413_109138853678114058_n.jpg', alt: 'Festival 3' },
      { src: '/images/516260494_17868346452406413_5390955057899085141_n.jpg', alt: 'Festival 4' }
    ]
  }
};

const FeaturedGallery: React.FC = () => {
  const [active, setActive] = useState<GalleryCategory['id']>('weddings');
  const current = galleryData[active];

  return (
    <section id="gallery" className="py-24 bg-friet-beige">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold mb-6">
            Event Inspiratie, <span className="text-friet-red">Beleef de Vreugde</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActive(tab.id)}
                className={`px-4 py-2 rounded-full text-sm sm:text-base border transition shadow-sm ${
                  active === tab.id
                    ? 'bg-friet-red text-white border-friet-red'
                    : 'bg-white text-friet-oak border-gray-200 hover:bg-red-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
          <div className="relative lg:col-span-2 h-[500px] rounded-lg overflow-hidden">
            <img src={current.main.src} alt={current.main.alt} className="w-full h-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 p-6 text-white bg-gradient-to-t from-black/70 to-transparent">
              {current.main.title && (
                <h4 className="text-xl font-serif font-bold mb-1">{current.main.title}</h4>
              )}
              {current.main.caption && (
                <p className="italic text-white/90">{current.main.caption}</p>
              )}
            </div>
          </div>

          <div className="grid grid-rows-2 gap-4 h-[500px]">
            {current.side.slice(0, 2).map((img, idx) => (
              <div key={idx} className="rounded-md overflow-hidden h-full">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover hover:scale-110 transition-transform duration-300" />
              </div>
            ))}
          </div>
        </div>

        {/* On xl screens, show four side tiles in a 2x2 grid next to main */}
        <div className="hidden xl:grid grid-cols-2 gap-4 mt-4">
          {current.side.slice(2, 4).map((img, idx) => (
            <div key={idx} className="rounded-md overflow-hidden h-60">
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover hover:scale-110 transition-transform duration-300" />
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link to="/gallery" className="bg-friet-red text-white px-6 py-3 rounded-full font-semibold hover:bg-red-700 transition-colors inline-flex items-center gap-2">
            <i className="fas fa-images"></i> Bekijk Volledige Gallerij
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedGallery; 